import { jwtDecode } from 'jwt-decode';
import {
  AUTH_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  USER_KEY,
} from '@/constants/auth.constants';
import { AuthUser } from './types';

/**
 * The single source of truth for persisted auth state.
 *
 * Every read/write of the access token, refresh token, and user record goes
 * through this module — nothing else touches localStorage for auth. That keeps
 * the three values from ever drifting out of sync, and gives the app one place
 * to reason about "are we still logged in?".
 */
export interface Session {
  user: AuthUser;
  accessToken: string;
  refreshToken: string;
}

export function isTokenValid(token: string | null): token is string {
  if (!token) return false;
  try {
    const { exp } = jwtDecode<{ exp?: number }>(token);
    // A decodable JWT with no exp claim → treat as non-expiring (the server stays
    // the final authority). Only a present exp in the past marks it expired.
    if (typeof exp !== 'number') return true;
    return exp * 1000 > Date.now();
  } catch {
    // Not a decodable token (corrupt/garbage storage) → never trust it.
    return false;
  }
}

export function getToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(AUTH_TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function getStoredUser(): AuthUser | null {
  if (typeof window === 'undefined') return null;
  const raw = localStorage.getItem(USER_KEY);
  if (!raw || raw === 'null') return null;
  try {
    return JSON.parse(raw) as AuthUser;
  } catch {
    return null;
  }
}

// Persist a freshly minted access token (called by the API client after a
// successful /auth/refresh). The refresh token and user are left untouched.
export function updateAccessToken(token: string): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(AUTH_TOKEN_KEY, token);
}

// Authenticated while EITHER token can still authorize us: a live access token,
// OR a refresh token we can still mint a new access token from. This is what
// keeps the guards from bouncing to /login before the API client gets a chance
// to silently refresh an expired access token.
export function isAuthenticated(): boolean {
  return isTokenValid(getToken()) || isTokenValid(getRefreshToken());
}

export function getSession(): Session | null {
  const user = getStoredUser();
  if (!user) return null;
  const accessToken = getToken();
  const refreshToken = getRefreshToken();
  // Keep the session only while we can still obtain a valid access token.
  if (!isTokenValid(accessToken) && !isTokenValid(refreshToken)) return null;
  return {
    user,
    accessToken: accessToken ?? '',
    refreshToken: refreshToken ?? '',
  };
}

export function setSession(session: Session): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(AUTH_TOKEN_KEY, session.accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, session.refreshToken);
  localStorage.setItem(USER_KEY, JSON.stringify(session.user));
}

export function clearSession(): void {
  if (typeof window === 'undefined') return;
  [AUTH_TOKEN_KEY, REFRESH_TOKEN_KEY, USER_KEY].forEach((k) =>
    localStorage.removeItem(k)
  );
}